import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Loader2, MapPin, AlertTriangle, Compass } from "lucide-react";
import { logger } from "@/lib/logger";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useTeamUsageQuery } from "@/hooks/useTeamUsageQuery";

const schema = z.object({
  address: z.string().min(1, "Address is required"),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  sellerName: z.string().optional(),
  zillowUrl: z
    .string()
    .url("Must be a valid Zillow URL")
    .refine((url) => url.includes("zillow.com"), "Must be a zillow.com URL")
    .optional()
    .or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

interface CreateCMADialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "form" | "loading" | "fallback";

export function CreateCMADialog({ open, onOpenChange }: CreateCMADialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getEffectiveUserId } = useImpersonation();
  const { canCreateCMA } = useTeamUsageQuery();
  const [step, setStep] = useState<Step>("form");
  const [loadingMessage, setLoadingMessage] = useState("Searching for property...");
  const [showFallback, setShowFallback] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      address: "",
      city: "",
      state: "",
      zip: "",
      sellerName: "",
      zillowUrl: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
      setStep("form");
      setShowFallback(false);
    }
  }, [open, form]);

  const createCMA = async (values: FormValues, zpid?: string, zillowUrl?: string, propertyData?: any) => {
    const effectiveUserId = await getEffectiveUserId();
    if (!effectiveUserId) throw new Error("Not authenticated");

    const { data: profile } = await supabase
      .from("profiles")
      .select("team_id, full_name, company_logo_url, default_primary_color, default_secondary_color")
      .eq("user_id", effectiveUserId)
      .maybeSingle();

    if (!profile?.team_id) throw new Error("Team not found. Please refresh and try again.");

    const addressFromData = propertyData?.address?.streetAddress || values.address;
    const cityFromData = propertyData?.address?.city || values.city || "";
    const stateFromData = propertyData?.address?.state || values.state || "";
    const zipFromData = propertyData?.address?.zipcode || values.zip || "";

    const { data: cma, error } = await supabase
      .from("cmas")
      .insert([
        {
          user_id: effectiveUserId,
          team_id: profile.team_id,
          address: addressFromData,
          city: cityFromData,
          state: stateFromData,
          zip: zipFromData,
          seller_name: values.sellerName || null,
          zpid: zpid || null,
          zillow_url: zillowUrl || null,
          property_data: propertyData || {},
          custom_agent_name: profile.full_name || null,
          custom_logo_url: profile.company_logo_url || null,
          primary_brand_color: profile.default_primary_color || "#000000",
          secondary_brand_color: profile.default_secondary_color || "#666666",
          is_processing: !!zpid,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return cma;
  };

  const onSubmit = async (values: FormValues) => {
    if (!canCreateCMA()) {
      toast({
        title: "Monthly limit reached",
        description: "You've used all your CMAs this month. Upgrade your plan to create more.",
        variant: "destructive",
      });
      return;
    }

    setStep("loading");
    setLoadingMessage("Searching for property...");

    try {
      // If user directly provided Zillow URL, skip address search
      if (values.zillowUrl && values.zillowUrl.trim()) {
        await handleZillowUrl(values, values.zillowUrl.trim());
        return;
      }

      // Try address search
      const { data: searchResult, error: searchError } = await supabase.functions.invoke(
        "search-property-by-address",
        {
          body: {
            address: values.address,
            city: values.city,
            state: values.state,
            zip: values.zip,
          },
        }
      );

      if (searchError || searchResult?.error || searchResult?.notFound) {
        logger.log("Address search failed, showing Zillow URL fallback");
        setStep("fallback");
        setShowFallback(true);
        return;
      }

      // Address search succeeded
      setLoadingMessage("Fetching comparable properties...");

      const cma = await createCMA(
        values,
        searchResult.zpid,
        searchResult.zillow_url,
        searchResult.property_data
      );

      // Trigger comps fetch in background — use full_address for off-market homes (no ZPID needed)
      if (cma?.id) {
        const fullAddress = searchResult.full_address ||
          [values.address, values.city, values.state, values.zip].filter(Boolean).join(" ");
        supabase.functions
          .invoke("fetch-cma-comps", {
            body: { zpid: searchResult.zpid || null, address: fullAddress, cmaId: cma.id },
          })
          .then(() => { logger.log("Comps fetched for CMA", cma.id); })
          .catch((err) => { logger.error("Failed to fetch comps:", err); });
      }

      onOpenChange(false);
      navigate(`/cma/${cma.id}`);
      toast({ title: "CMA Created!", description: "Your CMA is ready to edit." });
    } catch (err) {
      logger.error("Error creating CMA:", err);
      const msg = err instanceof Error ? err.message : "Failed to create CMA";
      toast({ title: "Error", description: msg, variant: "destructive" });
      setStep("form");
    }
  };

  const handleZillowUrl = async (values: FormValues, zillowUrl: string) => {
    try {
      setLoadingMessage("Fetching property from Zillow...");

      // Extract ZPID from the URL
      const zpidMatch = zillowUrl.match(/\/(\d+)_zpid/);
      const zpid = zpidMatch?.[1];

      if (!zpid) {
        toast({ title: "Invalid Zillow URL", description: "Could not extract property ID from URL.", variant: "destructive" });
        setStep("fallback");
        return;
      }

      // Use scrape-zillow to get full property data
      const { data: scrapedData, error: scrapeError } = await supabase.functions.invoke("scrape-zillow", {
        body: { zillowUrl },
      });

      let propertyData: any = null;
      if (!scrapeError && scrapedData) {
        propertyData = scrapedData;
      }

      setLoadingMessage("Creating CMA...");

      const cma = await createCMA(values, zpid, zillowUrl, propertyData);

      // Fetch comps in background
      supabase.functions
        .invoke("fetch-cma-comps", { body: { zpid, cmaId: cma.id } })
        .catch((err) => logger.error("Failed to fetch comps:", err));

      onOpenChange(false);
      navigate(`/cma/${cma.id}`);
      toast({ title: "CMA Created!", description: "Your CMA is ready to edit." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create CMA";
      toast({ title: "Error", description: msg, variant: "destructive" });
      setStep("fallback");
    }
  };

  const handleFallbackSubmit = async () => {
    const values = form.getValues();
    const zillowUrl = values.zillowUrl?.trim();

    if (!zillowUrl) {
      // Create CMA without Zillow data
      setStep("loading");
      setLoadingMessage("Creating CMA...");
      try {
        const cma = await createCMA(values);
        onOpenChange(false);
        navigate(`/cma/${cma.id}`);
        toast({ title: "CMA Created!", description: "Add comps manually from the editor." });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to create CMA";
        toast({ title: "Error", description: msg, variant: "destructive" });
        setStep("fallback");
      }
      return;
    }

    setStep("loading");
    await handleZillowUrl(values, zillowUrl);
  };

  if (step === "loading") {
    return (
      <Dialog open={true}>
        <DialogContent className="sm:max-w-[420px] gap-8 [&>button]:hidden">
          <div className="flex flex-col items-center space-y-5 py-6">
            <Compass className="h-12 w-12 text-primary animate-spin" style={{ animationDuration: "3s" }} />
            <div className="space-y-1 text-center">
              <h2 className="text-xl font-bold">Creating CMA</h2>
              <p className="text-sm text-muted-foreground">{loadingMessage}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            New CMA
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {showFallback && (
              <Alert variant="default" className="border-yellow-400 bg-yellow-50">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800 text-sm">
                  Couldn't find that address automatically. Paste the Zillow URL below, or skip it to create a manual CMA.
                </AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject Property Address *</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main Street" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-2">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="City" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <FormControl>
                      <Input placeholder="ST" maxLength={2} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zip</FormLabel>
                    <FormControl>
                      <Input placeholder="12345" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="sellerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Seller Name (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. John & Jane Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showFallback && (
              <FormField
                control={form.control}
                name="zillowUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zillow URL (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.zillow.com/homedetails/..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {showFallback ? (
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowFallback(false)}>
                  Back
                </Button>
                <Button type="button" className="flex-1" onClick={handleFallbackSubmit}>
                  Create CMA
                </Button>
              </div>
            ) : (
              <Button type="submit" variant="gold" className="w-full gap-2">
                <MapPin className="h-4 w-4" />
                Create CMA
              </Button>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
